import { NODE_SIZES, COLORS, ACTIVITY_THRESHOLDS, LEVEL_THRESHOLDS } from './constants';

/**
 * Get node color based on activity score and type
 */
export function getNodeColor(node) {
  if (node.type === 'temporary') {
    return COLORS.TEMPORARY;
  }

  const score = node.activity_score || 0;

  if (score >= ACTIVITY_THRESHOLDS.HIGH) {
    return {
      background: COLORS.ACTIVITY.HIGH.background,
      border: COLORS.ACTIVITY.HIGH.border,
      highlight: {
        background: COLORS.ACTIVITY.HIGH.border,
        border: COLORS.ACTIVITY.HIGH.border,
      },
    };
  } else if (score >= ACTIVITY_THRESHOLDS.MEDIUM) {
    return {
      background: COLORS.ACTIVITY.MEDIUM.background,
      border: COLORS.ACTIVITY.MEDIUM.border,
      highlight: {
        background: COLORS.ACTIVITY.MEDIUM.border,
        border: COLORS.ACTIVITY.MEDIUM.border,
      },
    };
  } else {
    return {
      background: COLORS.ACTIVITY.LOW.background,
      border: COLORS.ACTIVITY.LOW.border,
      highlight: {
        background: COLORS.ACTIVITY.LOW.border,
        border: COLORS.ACTIVITY.LOW.border,
      },
    };
  }
}

/**
 * Get node size based on level
 */
export function getNodeSize(level) {
  if (level === 0) {
    return NODE_SIZES.ROOT;
  } else if (level >= LEVEL_THRESHOLDS.DEEP_LEVEL_START) {
    return NODE_SIZES.DEEP_LEVEL;
  } else {
    return NODE_SIZES.STANDARD;
  }
}

/**
 * Get node label with optional activity score
 */
export function getNodeLabel(node) {
  if (node.type === 'user' && node.activity_score !== undefined) {
    return `${node.name}\n(Score: ${node.activity_score})`;
  }
  return node.name;
}

/**
 * Position nodes hierarchically - each node's children spread radially from it
 */
export function positionNodesHierarchically(nodes, edges, centerX, centerY, radialDistance = 180, arcSpan = Math.PI) {
  // Build parent-child map from edges
  const childrenMap = {};
  edges.forEach(edge => {
    if (!childrenMap[edge.from]) {
      childrenMap[edge.from] = [];
    }
    childrenMap[edge.from].push(edge.to);
  });
  
  // Find and position root node (level 0) at center
  const root = nodes.find(n => n.level === 0);
  if (!root) return;
  
  root.x = centerX;
  root.y = centerY;
  root.fx = centerX; // Fix root position
  root.fy = centerY;
  
  // Recursively position children
  function positionChildren(parentId, parentX, parentY, parentAngle = 0) {
    const children = childrenMap[parentId] || [];
    if (children.length === 0) return;
    
    // Calculate angle distribution
    const angleStart = parentAngle - arcSpan / 2;
    const angleStep = arcSpan / Math.max(children.length, 1);
    
    children.forEach((childId, i) => {
      const childNode = nodes.find(n => n.id === childId);
      if (!childNode) return;
      
      // Position child at angle from parent
      const angle = angleStart + (i * angleStep) + (angleStep / 2);
      childNode.x = parentX + radialDistance * Math.cos(angle);
      childNode.y = parentY + radialDistance * Math.sin(angle);
      
      // Recursively position this child's children
      positionChildren(childId, childNode.x, childNode.y, angle);
    });
  }
  
  // Start recursive positioning from root
  positionChildren(root.id, root.x, root.y, -Math.PI / 2); // Start from top
}

/**
 * Position nodes in concentric circles by level (legacy function - kept for compatibility)
 */
export function positionNodesByLevel(nodes, centerX, centerY, radius, levelSpacing) {
  const nodesByLevel = {};
  
  // Group nodes by level
  nodes.forEach(node => {
    if (!nodesByLevel[node.level]) {
      nodesByLevel[node.level] = [];
    }
    nodesByLevel[node.level].push(node);
  });
  
  // Position each level
  Object.keys(nodesByLevel)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .forEach(level => {
      const levelNodes = nodesByLevel[level];
      const levelNum = parseInt(level);
      
      if (levelNum === 0) {
        // Root at exact center
        levelNodes.forEach(node => {
          node.x = centerX;
          node.y = centerY;
          node.fx = centerX;
          node.fy = centerY;
        });
      } else {
        // Position nodes evenly around circle
        const nodeRadius = radius + ((levelNum - 1) * levelSpacing);
        const angleStep = (2 * Math.PI) / levelNodes.length;
        
        levelNodes.forEach((node, i) => {
          const angle = i * angleStep - Math.PI / 2;
          node.x = centerX + nodeRadius * Math.cos(angle);
          node.y = centerY + nodeRadius * Math.sin(angle);
        });
      }
    });
}

/**
 * Position nodes in inverted tree layout (bottom to top)
 * Root at bottom, tree grows upward
 */
export function positionNodesAsTree(nodes, edges, containerWidth, containerHeight) {
  // Constants
  const LEVEL_HEIGHT = 200; // Vertical distance between levels
  const MIN_SIBLING_GAP = 150; // Minimum horizontal gap between siblings
  const BOTTOM_PADDING = 100; // Space from bottom
  
  // Build parent-child relationships
  const childrenMap = {};
  edges.forEach(edge => {
    if (!childrenMap[edge.from]) {
      childrenMap[edge.from] = [];
    }
    childrenMap[edge.from].push(edge.to);
  });
  
  // Group nodes by level
  const nodesByLevel = {};
  nodes.forEach(node => {
    if (!nodesByLevel[node.level]) {
      nodesByLevel[node.level] = [];
    }
    nodesByLevel[node.level].push(node);
  });
  
  // Calculate subtree widths (how much horizontal space each node needs)
  const subtreeWidths = {};
  
  function calculateSubtreeWidth(nodeId) {
    if (subtreeWidths[nodeId]) return subtreeWidths[nodeId];
    
    const children = childrenMap[nodeId] || [];
    if (children.length === 0) {
      subtreeWidths[nodeId] = MIN_SIBLING_GAP;
      return MIN_SIBLING_GAP;
    }
    
    let totalWidth = 0;
    children.forEach(childId => {
      totalWidth += calculateSubtreeWidth(childId);
    });
    
    subtreeWidths[nodeId] = Math.max(totalWidth, MIN_SIBLING_GAP);
    return subtreeWidths[nodeId];
  }
  
  // Calculate widths for all nodes starting from leaves
  const maxLevel = Math.max(...nodes.map(n => n.level));
  for (let level = maxLevel; level >= 0; level--) {
    nodesByLevel[level]?.forEach(node => {
      calculateSubtreeWidth(node.id);
    });
  }
  
  // Calculate total height needed
  const requiredHeight = BOTTOM_PADDING + (maxLevel * LEVEL_HEIGHT) + 100;
  
  // Position nodes level by level
  const positionedNodes = {};
  
  function positionNode(nodeId, x, y) {
    const node = nodes.find(n => n.id === nodeId);
    if (!node || positionedNodes[nodeId]) return;
    
    node.x = x;
    node.y = y;
    node.fx = x; // Fix position
    node.fy = y;
    positionedNodes[nodeId] = true;
    
    // Position children (they go UP, not down)
    const children = childrenMap[nodeId] || [];
    if (children.length === 0) return;
    
    // Calculate total width needed for children
    const totalWidth = subtreeWidths[nodeId];
    let currentX = x - (totalWidth / 2);
    
    children.forEach(childId => {
      const childWidth = subtreeWidths[childId];
      const childCenterX = currentX + (childWidth / 2);
      const childY = y - LEVEL_HEIGHT; // SUBTRACT to go UP
      
      positionNode(childId, childCenterX, childY);
      currentX += childWidth;
    });
  }
  
  // Start from root at BOTTOM center
  const root = nodes.find(n => n.level === 0);
  if (root) {
    const rootX = containerWidth / 2;
    const rootY = requiredHeight - BOTTOM_PADDING; // Bottom position
    positionNode(root.id, rootX, rootY);
  }
  
  return requiredHeight;
}

